/**
 * Stories That Inspire Carousel Frontend Script
 * 
 * Handles carousel navigation, auto-swipe, and responsive behavior
 */

import './frontend.css';

(function() {
    'use strict';

    /**
     * Initialize carousel for a specific block
     */
    function initCarousel(blockId, settings) {
        const block = document.getElementById(blockId);
        if (!block) return;

        const carousel = block.querySelector('.flex[data-current-slide]');
        if (!carousel) return; // Grid layout, no carousel needed

        const slides = block.querySelectorAll('[data-slide-index]');
        const prevBtn = block.querySelector('.stories-carousel-prev');
        const nextBtn = block.querySelector('.stories-carousel-next');
        const dots = block.querySelectorAll('.stories-carousel-dot');

        if (!carousel || slides.length === 0) return;

        const totalSlides = slides.length;
        let currentSlide = 0;
        let autoSwipeInterval = null;

        const autoSwipe = settings.autoSwipe || false;
        const autoTime = settings.autoTime || 5000;
        const infinite = settings.infinite !== false; // Default to true

        // Get number of visible slides based on viewport
        function getVisibleSlides() {
            const width = window.innerWidth;
            if (width >= 1024) return 3; // lg: 3 columns
            if (width >= 768) return 2;  // md: 2 columns
            return 1; // mobile: 1 column
        }


        // Update carousel position
        function updateCarousel() {
            const visibleSlides = getVisibleSlides();
            const maxSlide = Math.max(0, totalSlides - visibleSlides);
            
            // Clamp currentSlide to valid range
            if (!infinite) {
                currentSlide = Math.max(0, Math.min(currentSlide, maxSlide));
            }

            // Calculate transform percentage
            // Each slide takes 100% width, so we move by 100% / visibleSlides
            const translateX = -(currentSlide * (100 / visibleSlides));
            carousel.style.transform = `translateX(${translateX}%)`;
            carousel.setAttribute('data-current-slide', currentSlide);

            // Update dots
            dots.forEach((dot, index) => {
                if (index === currentSlide) {
                    dot.classList.add('bg-primary', 'w-6');
                    dot.classList.remove('bg-gray-300', 'w-2');
                } else {
                    dot.classList.remove('bg-primary', 'w-6');
                    dot.classList.add('bg-gray-300', 'w-2');
                }
            });

            // Update button states
            if (!infinite) {
                if (prevBtn) {
                    prevBtn.disabled = currentSlide === 0;
                    prevBtn.classList.toggle('opacity-50', currentSlide === 0);
                    prevBtn.classList.toggle('cursor-not-allowed', currentSlide === 0);
                }
                if (nextBtn) {
                    nextBtn.disabled = currentSlide >= maxSlide;
                    nextBtn.classList.toggle('opacity-50', currentSlide >= maxSlide);
                    nextBtn.classList.toggle('cursor-not-allowed', currentSlide >= maxSlide);
                }
            } else {
                if (prevBtn) {
                    prevBtn.disabled = false;
                    prevBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                }
                if (nextBtn) {
                    nextBtn.disabled = false;
                    nextBtn.classList.remove('opacity-50', 'cursor-not-allowed');
                }
            }
        }

        // Go to next slide
        function goToNext() {
            const visibleSlides = getVisibleSlides();
            const maxSlide = Math.max(0, totalSlides - visibleSlides);

            if (infinite) {
                currentSlide = (currentSlide + 1) % (maxSlide + 1);
            } else {
                currentSlide = Math.min(currentSlide + 1, maxSlide);
            }
            updateCarousel();
        }

        // Go to previous slide
        function goToPrev() {
            const visibleSlides = getVisibleSlides();
            const maxSlide = Math.max(0, totalSlides - visibleSlides);

            if (infinite) {
                currentSlide = currentSlide === 0 ? maxSlide : currentSlide - 1;
            } else {
                currentSlide = Math.max(currentSlide - 1, 0);
            }
            updateCarousel();
        }

        // Go to specific slide
        function goToSlide(index) {
            const visibleSlides = getVisibleSlides();
            const maxSlide = Math.max(0, totalSlides - visibleSlides);
            currentSlide = Math.max(0, Math.min(index, maxSlide));
            updateCarousel();
        }

        // Start auto-swipe
        function startAutoSwipe() {
            if (!autoSwipe) return;
            stopAutoSwipe();
            autoSwipeInterval = setInterval(goToNext, autoTime);
        }

        // Stop auto-swipe
        function stopAutoSwipe() {
            if (autoSwipeInterval) {
                clearInterval(autoSwipeInterval);
                autoSwipeInterval = null;
            }
        }

        // Event listeners
        if (nextBtn) {
            nextBtn.addEventListener('click', () => {
                goToNext();
                stopAutoSwipe();
                startAutoSwipe();
            });
        }

        if (prevBtn) {
            prevBtn.addEventListener('click', () => {
                goToPrev();
                stopAutoSwipe();
                startAutoSwipe();
            });
        }

        // Dot navigation
        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                goToSlide(index);
                stopAutoSwipe();
                startAutoSwipe();
            });
        });

        // Pause auto-swipe on hover
        if (autoSwipe) {
            block.addEventListener('mouseenter', stopAutoSwipe);
            block.addEventListener('mouseleave', startAutoSwipe);
        }

        // Handle window resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                updateCarousel();
            }, 250);
        }, { passive: true });

        // Keyboard navigation
        block.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goToPrev();
                stopAutoSwipe();
                startAutoSwipe();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                goToNext();
                stopAutoSwipe();
                startAutoSwipe();
            }
        });

        // Initialize
        updateCarousel();
        startAutoSwipe();

        // Make carousel focusable for keyboard navigation
        block.setAttribute('tabindex', '0');
    }

    /**
     * Initialize all carousels on the page
     */
    function init() {
        // Wait for settings to be available
        if (typeof window.storiesCarouselSettings === 'undefined') {
            // Retry after a short delay
            setTimeout(init, 100);
            return;
        }

        // Initialize each carousel
        Object.keys(window.storiesCarouselSettings).forEach((blockId) => {
            const settings = window.storiesCarouselSettings[blockId];
            initCarousel(blockId, settings);
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

